import React from 'react';
import PropTypes from 'prop-types';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

import addTrailingSlash from '../../utils/url/addTrailingSlash';

import BreadCrumb from './components/BreadCrumb/BreadCrumb';
import CorporateContact from './components/CorporateContact/CorporateContact';
import AlternatePages from './components/AlternatePages/AlternatePages';

const SEO = ({
  pageTitle,
  description,
  domain,
  breadcrumbTitle,
  noindex,
  nofollow,
  alternatePages,
  canonicalPage,
  twitterCardType,
  images,
  baseBreadcrumb,
  corporateContactData,
}) => {
  const { pathname, asPath = '/' } = useRouter();
  const canonicalURL = `${domain}${addTrailingSlash(canonicalPage || asPath)}`;

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={description}
        noindex={noindex}
        nofollow={nofollow}
        canonical={canonicalURL}
        openGraph={{
          url: domain,
          site_name: 'Nubank',
          type: 'website',
          title: pageTitle,
          description,
          images,
        }}
        twitter={{
          cardType: twitterCardType,
        }}
      />
      <AlternatePages
        pathname={pathname}
        alternatePages={alternatePages}
        domain={domain}
      />
      {baseBreadcrumb && (
      <BreadCrumb
        domain={domain}
        pathname={pathname}
        title={breadcrumbTitle}
        baseBreadcrumb={baseBreadcrumb}
      />
      )}
      {corporateContactData && <CorporateContact corporateContactData={corporateContactData} />}
    </>
  );
};

SEO.defaultProps = {
  alternatePages: {},
  breadcrumbTitle: null,
  canonicalPage: undefined,
  description: undefined,
  twitterCardType: 'summary_large_image',
  noindex: false,
  nofollow: false,
};

SEO.propTypes = {
  alternatePages: PropTypes.objectOf(PropTypes.string),
  baseBreadcrumb: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.string,
      name: PropTypes.string,
      position: PropTypes.number,
    }),
  ).isRequired,
  breadcrumbTitle: PropTypes.string,
  canonicalPage: PropTypes.string,
  corporateContactData: PropTypes.shape({
    contactPoint: PropTypes.arrayOf(
      PropTypes.shape({
        areaServed: PropTypes.string,
        availableLanguage: PropTypes.arrayOf(PropTypes.string),
        contactOption: PropTypes.string,
        contactType: PropTypes.string,
        telephone: PropTypes.string,
      }),
    ),
    logo: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  description: PropTypes.string,
  domain: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      height: PropTypes.number,
      url: PropTypes.string,
      width: PropTypes.number,
    }),
  ).isRequired,
  nofollow: PropTypes.bool,
  noindex: PropTypes.bool,
  pageTitle: PropTypes.string.isRequired,
  twitterCardType: PropTypes.oneOf(['app', 'summary', 'summary_large_image', 'video']),
};

export default SEO;
